import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
    ImageIcon,
    CpuIcon,
    BarChart3Icon,
    ArrowLeftIcon,
    SproutIcon,
    UploadCloudIcon,
    LayersIcon,
    ScanLineIcon,
    EyeOffIcon,
    FolderIcon,
    RouterIcon,
} from 'lucide-react';
import logo from '@/assets/Humeos_logotype_RGB_positive.png';
import { BASE } from '../../basePath';
import { useGetUser } from '@/services/auth.service';
import { useHiddenSamples } from '@/services/sample.service';

const NAV = [
    {
        to: `${BASE}/backOffice/monitoring`,
        label: 'Monitoring',
        icon: SproutIcon,
    },
    {
        to: `${BASE}/backOffice/projects`,
        label: 'Projects',
        icon: FolderIcon,
    },
    { to: `${BASE}/backOffice/hubs`, label: 'Hubs', icon: RouterIcon },
    {
        to: `${BASE}/backOffice/scanners`,
        label: 'Scanners',
        icon: ScanLineIcon,
    },
    {
        to: `${BASE}/backOffice/samples`,
        label: 'Samples',
        icon: LayersIcon,
    },
    { to: `${BASE}/backOffice/images`, label: 'Images', icon: ImageIcon },
    { to: `${BASE}/backOffice/hide`, label: 'Hide', icon: EyeOffIcon },
    { to: `${BASE}/backOffice/compute`, label: 'Compute', icon: CpuIcon },
    { to: `${BASE}/backOffice/tracking`, label: 'Tracking', icon: BarChart3Icon },
    {
        to: `${BASE}/backOffice/transfer`,
        label: 'Transfer links',
        icon: UploadCloudIcon,
    },
];

export default function BackOfficeLayout() {
    const location = useLocation();
    const { data: user, isLoading } = useGetUser();
    // Unread-style counter on the Hide entry, so a forgotten hidden sample
    // stays in sight.
    const { data: hiddenSamples } = useHiddenSamples();
    const badges: Record<string, number> = {
        [`${BASE}/backOffice/hide`]: hiddenSamples?.length ?? 0,
    };

    if (isLoading) return null;
    if (!user?.isSuperAdmin) return <Navigate to={`${BASE}/project`} replace />;

    return (
        <div className="flex h-screen w-screen bg-[#0f141a] text-[#e5e7e3]">
            <aside className="w-60 flex-shrink-0 bg-[#0a0e12] border-r border-[#1e242a] flex flex-col">
                <div className="h-16 px-5 flex items-center border-b border-[#1e242a]">
                    <Link
                        to={`${BASE}/backOffice`}
                        className="flex items-center gap-2.5"
                    >
                        <img src={logo} alt="Humeos" className="h-8 w-8" />
                        <div className="flex flex-col leading-tight">
                            <span className="text-sm font-semibold text-white">
                                Backoffice
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                                Internal admin
                            </span>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        const active = location.pathname.startsWith(item.to);
                        const badge = badges[item.to] ?? 0;
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                                    active
                                        ? 'bg-[#1e2a22] text-white'
                                        : 'text-[#a8b0bb] hover:bg-[#161c22] hover:text-white'
                                }`}
                            >
                                <Icon
                                    className={`h-4 w-4 ${
                                        active
                                            ? 'text-[#74c69d]'
                                            : 'text-[#6b7480]'
                                    }`}
                                />
                                {item.label}
                                {badge > 0 && (
                                    <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center">
                                        {badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="border-t border-[#1e242a] p-3">
                    <Link
                        to={`${BASE}/project`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#a8b0bb] hover:bg-[#161c22] hover:text-white transition"
                    >
                        <ArrowLeftIcon className="h-3.5 w-3.5" />
                        Back to platform
                    </Link>
                </div>
            </aside>

            <main className="flex-1 overflow-auto bg-[#0f141a]">
                <Outlet />
            </main>
        </div>
    );
}
